import React, { useState } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import styles from "./index.module.scss"

const DEFAULT_SCROLL_AFTER = 12

/** Multi choice list of checkboxes, optionally collapsed after `collapseAfter` options. */
const CheckboxGroup = ({
  id,
  label,
  options,
  values,
  onChange,
  collapseAfter,
  columns,
  scrollAfter,
}) => {
  const canCollapse = Boolean(collapseAfter) && options.length > collapseAfter
  const hiddenSelected =
    canCollapse &&
    options.slice(collapseAfter).some((option) => values.includes(option.id))
  const [isExpanded, setIsExpanded] = useState(hiddenSelected)
  const visibleOptions =
    isExpanded || !canCollapse ? options : options.slice(0, collapseAfter)
  // Very long lists scroll inside the group instead of stretching the page, unless `scrollAfter` is null.
  const isScrollable =
    Boolean(scrollAfter) && visibleOptions.length > scrollAfter

  const toggle = (optionId) =>
    onChange(
      values.includes(optionId)
        ? values.filter((value) => value !== optionId)
        : [...values, optionId],
    )

  return (
    <fieldset className={styles.group}>
      <legend className={styles.label}>
        {label}
        {values.length > 0 && (
          <span className={styles.count}>{values.length}</span>
        )}
      </legend>
      <div
        className={classNames(styles.options, styles[`columns${columns}`], {
          [styles.scrollable]: isScrollable,
        })}
      >
        {visibleOptions.map((option) => {
          const inputId = `${id}-${option.id}`
          const checked = values.includes(option.id)
          return (
            <label
              key={option.id}
              htmlFor={inputId}
              className={classNames(styles.option, {
                [styles.checked]: checked,
              })}
            >
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(option.id)}
              />
              <span className={styles.box} aria-hidden="true" />
              <span className={styles.text}>{option.value}</span>
            </label>
          )
        })}
      </div>
      {canCollapse && (
        <button
          type="button"
          className={styles.more}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded
            ? "Show less"
            : `Show all (${options.length - collapseAfter} more)`}
        </button>
      )}
    </fieldset>
  )
}

CheckboxGroup.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, value: PropTypes.string }),
  ).isRequired,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  /** Number of options shown before "Show all"; falsy shows every option */
  collapseAfter: PropTypes.number,
  columns: PropTypes.oneOf([1, 2, 3]),
  /** Number of options after which the list scrolls; null shows every option without a scrollbar */
  scrollAfter: PropTypes.number,
}

CheckboxGroup.defaultProps = {
  collapseAfter: 5,
  columns: 1,
  scrollAfter: DEFAULT_SCROLL_AFTER,
}

export default CheckboxGroup
